/**
 * @fileoverview History (Audit Trail) Animation
 *
 * Purpose: A stylised, looping recreation of Morgan's in-app "History" panel
 * for the homepage integrations card ("Built for control, audit &
 * compliance."). It communicates the product's full audit trail — every
 * action attributed to a practitioner or an agent, with before/after diffs.
 *
 * It mirrors the real `ChangeHistory` component (shared-panels/ChangeHistory):
 * a collapsible "HISTORY" header with a count badge, then a feed of entries.
 * Each entry has an avatar (practitioner initials) or an actor icon (Agent /
 * AI / System), a coloured actor badge, a right-aligned date, a reason line,
 * and — for field changes — a `before -> after` diff card (rose strike-through
 * old value, emerald new value).
 *
 * Motion: a pure-CSS vertical marquee (reuses the `marquee-vertical` keyframes
 * in tailwind.config.ts). The feed is rendered twice back-to-back so the
 * translateY(-50%) loop is seamless. Top/bottom mask gradients fade the feed
 * edges. Respects prefers-reduced-motion via Tailwind's `motion-reduce`
 * variant (the feed simply rests).
 *
 * This is a marketing stylisation — no live data — sized to the integration
 * card container. Theming uses the global semantic CSS variables so it adapts
 * to light and dark mode.
 *
 * Used by: IntegrationsSection (-sections.integrations.tsx)
 */

import { ArrowRight, Bot, ChevronDown, Database, Sparkles } from "lucide-react";
import { cn } from "@/utils/misc";

/** The kind of actor that performed an action (drives the badge + icon). */
type ActorKind = "user" | "agent" | "ai" | "system";

/** A single audit-trail entry shown in the scrolling history feed. */
interface HistoryItem {
  /** Actor type — drives badge colour + avatar/icon. */
  kind: ActorKind;
  /** Display name (practitioner) or actor label (Agent / AI / System). */
  label: string;
  /** Initials shown in the avatar (practitioner entries only). */
  initials?: string;
  /** Short, right-aligned date string. */
  date: string;
  /** The action summary line. */
  reason: string;
  /** Optional before/after field diff card. */
  diff?: { field: string; before: string; after: string };
}

/**
 * Stylised slice of a job detail panel's audit trail. Mirrors the real
 * `ChangeHistory` actor taxonomy (practitioner / Agent / AI / System) so the
 * card communicates "every action is attributed and auditable".
 */
const HISTORY: HistoryItem[] = [
  {
    kind: "user",
    label: "Stuart Anderson",
    initials: "SA",
    date: "29 Jun 2026",
    reason: "Override recovery $0–$0",
    diff: { field: "High", before: "100,000", after: "0" },
  },
  {
    kind: "user",
    label: "Stuart Anderson",
    initials: "SA",
    date: "29 Jun 2026",
    reason: "Manually selected",
  },
  {
    kind: "agent",
    label: "Agent",
    date: "22 Jun 2026",
    reason: "Estimated recovery $0–$100,000",
  },
  {
    kind: "agent",
    label: "Agent",
    date: "22 Jun 2026",
    reason:
      "ROCAP adds director estimate of $185,000 (vs $252,000 per ledger) — a 36% gap requiring a valuation_mismatch flag.",
  },
  {
    kind: "user",
    label: "Sarah Nguyen",
    initials: "SN",
    date: "20 Jun 2026",
    reason: "Updated nature of debt",
    diff: { field: "Type", before: "Unsecured", after: "Secured" },
  },
  {
    kind: "system",
    label: "System",
    date: "18 Jun 2026",
    reason: "Linked security interest to asset",
  },
];

/** Per-actor display config — mirrors `getActorInfo` in ChangeHistory. */
const ACTOR_STYLES: Record<
  Exclude<ActorKind, "user">,
  { Icon: typeof Bot; badge: string; bg: string; icon: string }
> = {
  agent: {
    Icon: Bot,
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
    bg: "bg-blue-50 dark:bg-blue-950/30",
    icon: "text-blue-600 dark:text-blue-400",
  },
  ai: {
    Icon: Sparkles,
    badge:
      "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    bg: "bg-amber-50 dark:bg-amber-950/30",
    icon: "text-amber-600 dark:text-amber-400",
  },
  system: {
    Icon: Database,
    badge: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
    bg: "bg-sky-50 dark:bg-sky-950/30",
    icon: "text-sky-600 dark:text-sky-400",
  },
};

/**
 * Animated, looping recreation of an in-app detail panel's audit trail.
 *
 * @component
 * @example
 * <HistoryAnimation />
 */
export function HistoryAnimation() {
  return (
    <div
      className="absolute inset-0 flex flex-col overflow-hidden rounded-lg border border-primary/10 bg-white shadow-sm dark:bg-card"
      aria-hidden="true"
    >
      {/* History header (mirrors the collapsible trigger) */}
      <div className="flex items-center justify-between border-b border-primary/10 bg-primary/[0.03] px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary/55">
            History
          </span>
          <span className="rounded-md bg-primary/[0.08] px-1.5 py-px text-[9px] font-semibold tabular-nums text-primary/60">
            12
          </span>
        </div>
        <ChevronDown className="h-3.5 w-3.5 text-primary/40" />
      </div>

      {/* Scrolling history feed (masked at top + bottom) */}
      <div
        className="relative min-h-0 flex-1 overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)",
        }}
      >
        <div className="flex animate-marquee-vertical flex-col px-3 motion-reduce:animate-none">
          {/* Rendered twice for a seamless translateY(-50%) loop */}
          {[...HISTORY, ...HISTORY].map((item, i) => (
            <HistoryEntry key={`${item.label}-${i}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** A single history row, mirroring the `ChangeHistory` entry layout. */
function HistoryEntry({ item }: { item: HistoryItem }) {
  return (
    <div className="flex items-start gap-2.5 border-b border-border/50 py-3">
      {/* Avatar (practitioner) or actor icon */}
      {item.kind === "user" ? (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-[9px] font-semibold text-primary-foreground">
          {item.initials}
        </span>
      ) : (
        (() => {
          const style = ACTOR_STYLES[item.kind];
          return (
            <span className={cn("shrink-0 rounded-md p-1.5", style.bg)}>
              <style.Icon className={cn("h-3.5 w-3.5", style.icon)} />
            </span>
          );
        })()
      )}

      <div className="min-w-0 flex-1">
        {/* Badge + date row */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              "rounded-md px-1.5 py-0.5 text-[9px] font-semibold",
              item.kind === "user"
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                : ACTOR_STYLES[item.kind].badge,
            )}
          >
            {item.label}
          </span>
          <span className="shrink-0 text-[8.5px] tabular-nums text-muted-foreground">
            {item.date}
          </span>
        </div>

        {/* Reason line */}
        <p className="mt-1 line-clamp-3 text-[10px] leading-relaxed text-muted-foreground">
          {item.reason}
        </p>

        {/* Before -> after diff card */}
        {item.diff && (
          <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5">
            <span className="text-[8px] font-medium uppercase tracking-wider text-muted-foreground">
              {item.diff.field}
            </span>
            <span className="ml-auto rounded-sm bg-rose-50 px-1 py-0.5 text-[9px] tabular-nums text-rose-900 line-through dark:bg-rose-950/40 dark:text-rose-200">
              {item.diff.before}
            </span>
            <ArrowRight className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
            <span className="rounded-sm bg-emerald-50 px-1 py-0.5 text-[9px] tabular-nums text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
              {item.diff.after}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
